// Özel boyut yönetimi paneli (TODO-KPI-146 / K-B4, spec 2026-11-02 §8a): Katalog "Boyutları
// yönet" penceresinin gövdesi. Hazır boyutlar listelenmez (değiştirilemez); özel boyutlar için
// satır başına Düzenle (ad, üye şablonu, uygun kategoriler) ve Sil (onaylı; kullanan grup varsa
// sunucu 409 → sebep metni). Görünüm + olay: kayıt/silme `onSave`/`onDelete` ile üstte; dönen
// string hata metnidir (null = başarı).

import { useState } from "react";
import { KPI_ROOT_CODES, type KpiBreakdownCustomDimension } from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import {
  formFromDimension,
  patchFromForm,
  type DimensionEditForm,
} from "../../lib/kpi-breakdown-dimension-manage";
import { type KpiBreakdownCustomDimensionPatch } from "@mahir/shared";
import { ConfirmDialog } from "../common/ConfirmDialog";
import {
  btnGhost,
  btnPrimary,
  btnSmall,
  fieldStyle,
  inputStyle,
  labelStyle,
} from "./kpi-form-styles";

const C = KPI_BREAKDOWN_COPY.dimensions;
const help = { fontSize: "0.875rem", color: "var(--color-text-3)" } as const;

export interface BreakdownDimensionsPanelProps {
  dimensions: readonly KpiBreakdownCustomDimension[];
  onSave: (slug: string, patch: KpiBreakdownCustomDimensionPatch) => Promise<string | null>;
  onDelete: (slug: string) => Promise<string | null>;
  /** SSR testleri: düzenleme kipinde başlat */
  initialEditing?: string | undefined;
  /** SSR testleri: silme onayı açık başlat */
  initialRemoving?: string | undefined;
  initialError?: string | undefined;
}

export function BreakdownDimensionsPanel(props: BreakdownDimensionsPanelProps): JSX.Element {
  const [editing, setEditing] = useState<string | null>(props.initialEditing ?? null);
  const [removing, setRemoving] = useState<string | null>(props.initialRemoving ?? null);
  const [form, setForm] = useState<DimensionEditForm | null>(() => {
    const d = props.dimensions.find((x) => x.slug === props.initialEditing);
    return d ? formFromDimension(d) : null;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(props.initialError ?? null);

  async function run(fn: () => Promise<string | null>, done: () => void): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const err = await fn();
      if (err) setError(err);
      else done();
    } finally {
      setBusy(false);
    }
  }

  const removingDim = props.dimensions.find((d) => d.slug === removing) ?? null;

  return (
    <div className="grid gap-3" data-testid="kpi-dimensions-panel">
      <p style={{ ...help, margin: 0 }}>{C.intro}</p>
      {error ? (
        <div
          role="alert"
          data-testid="kpi-dimensions-error"
          className="rounded-lg px-3 py-2 text-sm"
          style={{
            background: "var(--color-warning-bg)",
            border: "1px solid var(--color-warning-border)",
            color: "var(--color-warning-text)",
          }}
        >
          {error}
        </div>
      ) : null}
      {props.dimensions.length === 0 ? <p style={help}>{C.empty}</p> : null}
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
        {props.dimensions.map((d) => {
          const isEditing = editing === d.slug && form !== null;
          return (
            <li
              key={d.slug}
              className="rounded-lg"
              style={{ border: "1px solid var(--color-border)", padding: 12 }}
              data-testid={`kpi-dimension-${d.slug}`}
            >
              {isEditing ? (
                <EditForm
                  slug={d.slug}
                  form={form}
                  busy={busy}
                  original={d}
                  onChange={setForm}
                  onCancel={() => {
                    setEditing(null);
                    setForm(null);
                    setError(null);
                  }}
                  onSave={(patch) =>
                    void run(
                      () => props.onSave(d.slug, patch),
                      () => {
                        setEditing(null);
                        setForm(null);
                      },
                    )
                  }
                />
              ) : (
                <div className="flex items-start gap-3 flex-wrap">
                  <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{d.label}</div>
                    <div style={help}>
                      {d.members.length > 0
                        ? `${C.members(d.members.length)}: ${d.members.join(", ")}`
                        : C.noMembers}
                    </div>
                    <div style={help}>
                      {d.categories.length > 0
                        ? d.categories.map((c) => C.roots[c]).join(" · ")
                        : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{ ...btnSmall, minHeight: 44 }}
                    disabled={busy}
                    data-testid="kpi-dimension-edit"
                    onClick={() => {
                      setEditing(d.slug);
                      setForm(formFromDimension(d));
                      setError(null);
                    }}
                  >
                    {C.edit}
                  </button>
                  <button
                    type="button"
                    style={{ ...btnSmall, minHeight: 44, color: "var(--color-error)" }}
                    disabled={busy}
                    data-testid="kpi-dimension-remove"
                    onClick={() => {
                      setRemoving(d.slug);
                      setError(null);
                    }}
                  >
                    {C.remove}
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        open={removingDim !== null}
        title={removingDim ? C.removeConfirmTitle(removingDim.label) : ""}
        body={C.removeConfirmBody}
        confirmLabel={C.removeConfirm}
        tone="danger"
        busy={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const slug = removing;
          if (!slug) return;
          void run(
            () => props.onDelete(slug),
            () => setRemoving(null),
          ).then(() => setRemoving(null));
        }}
      />
    </div>
  );
}

function EditForm(props: {
  slug: string;
  form: DimensionEditForm;
  original: KpiBreakdownCustomDimension;
  busy: boolean;
  onChange: (f: DimensionEditForm) => void;
  onCancel: () => void;
  onSave: (patch: KpiBreakdownCustomDimensionPatch) => void;
}): JSX.Element {
  const { form } = props;
  const patch = patchFromForm(props.original, form);
  const id = `kpi-dim-${props.slug}`;
  return (
    <div className="grid gap-1">
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor={`${id}-label`}>
          {C.labelLabel}
        </label>
        <input
          id={`${id}-label`}
          style={inputStyle}
          value={form.label}
          maxLength={80}
          disabled={props.busy}
          onChange={(e) => props.onChange({ ...form, label: e.target.value })}
        />
        <span style={help}>{C.slugLine(props.slug)}</span>
      </div>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor={`${id}-members`}>
          {C.membersLabel}
        </label>
        <textarea
          id={`${id}-members`}
          style={{ ...inputStyle, minHeight: 96 }}
          value={form.membersText}
          disabled={props.busy}
          onChange={(e) => props.onChange({ ...form, membersText: e.target.value })}
        />
      </div>
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend style={labelStyle}>{C.categoriesLabel}</legend>
        <div className="flex gap-3 flex-wrap">
          {KPI_ROOT_CODES.map((code) => (
            <label key={code} className="flex items-center gap-1" style={{ minHeight: 44 }}>
              <input
                type="checkbox"
                checked={form.categories.includes(code)}
                disabled={props.busy}
                onChange={(e) =>
                  props.onChange({
                    ...form,
                    categories: e.target.checked
                      ? [...form.categories, code]
                      : form.categories.filter((c) => c !== code),
                  })
                }
              />
              {C.roots[code]}
            </label>
          ))}
        </div>
        <span style={help}>{C.categoriesHint}</span>
      </fieldset>
      <div className="flex gap-2 justify-end" style={{ marginTop: 8 }}>
        <button type="button" style={btnGhost} disabled={props.busy} onClick={props.onCancel}>
          {C.cancel}
        </button>
        <button
          type="button"
          style={{ ...btnPrimary, opacity: patch && !props.busy ? 1 : 0.5 }}
          disabled={!patch || props.busy}
          data-testid="kpi-dimension-save"
          onClick={() => patch && props.onSave(patch)}
        >
          {props.busy ? C.saving : C.save}
        </button>
      </div>
    </div>
  );
}
